import { useCallback } from 'react';
import { css } from '@emotion/react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { RadioCardField, Text, useAlert, useFormField } from '@frontend/design-system';
import { Step } from './atoms/step';
import { AddStepPanelProps } from './types';
import { getRepeatPhoneTreeName } from './utils';

export type PhoneTreeAncestors = Record<
  string,
  { node: CallRouteV1.Types.Node; dialOptionNodes: CallRouteV1.Types.Node[] }
>;

type RepeatStepProps = AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT> & {
  phoneTreeNodes: PhoneTreeAncestors;
};

export const RepeatStep = ({ initialState, phoneTreeNodes, onProceedClick }: RepeatStepProps) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();

  const cardFieldProps = useFormField({
    required: true,
    type: 'radio',
    value: initialState?.callObject?.repeatExpansion?.phoneTreeNodeId,
  });

  const handleProceedClick = useCallback(() => {
    const node = phoneTreeNodes[cardFieldProps.value].node;
    if (!node) {
      alerts.error('Unknown phone tree data');
      sentry.error({
        error: 'Phone tree node not found in Repeat step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            phoneTreeNodes,
            selectedNodeId: cardFieldProps.value,
          },
        },
      });
      return;
    }

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT,
      callObject: {
        primitiveId: initialState?.callObject?.primitiveId ?? '',
        primitiveName: getRepeatPhoneTreeName(node),
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        repeatExpansion: {
          phoneTreeNodeId: node.id,
        },
      },
    });
  }, [phoneTreeNodes, cardFieldProps.value, alerts, onProceedClick, initialState]);

  return (
    <Step
      title={t('Repeat Phone Tree')}
      primaryButtonDisabled={!cardFieldProps.value}
      onPrimaryButtonClick={handleProceedClick}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Text weight='bold'>{t('Select Phone Tree')}</Text>
        <Text size='medium'>{t('Select which previous phone tree will repeat when a caller reaches this step. ')}</Text>
      </div>

      <RadioCardField {...cardFieldProps} name='phoneTree' css={cardFieldStyles}>
        {Object.values(phoneTreeNodes).map(({ node, dialOptionNodes }) => (
          <RadioCardField.Option
            key={node.id}
            css={cardStyles}
            value={node.id}
            title={node.callObject.primitiveName}
            description={t('{{count}} Dial Options', { count: dialOptionNodes.length })}
          />
        ))}
        <span></span>
      </RadioCardField>
    </Step>
  );
};

const cardStyles = css`
  flex: unset;
  width: 300px;
`;
const cardFieldStyles = css`
  div:first-child {
    display: flex;
    flex-wrap: wrap;
  }
`;
